import TWEEN, { Easing } from "@tweenjs/tween.js"
import { animations, cleanAnimations, durations, expGameInstance, GameAnimation, mdlHeights, scene } from "../ThreeTestFC";
import { Character, GameInstance, SubInstance } from "../../../types";
import * as THREE from "three";
import * as lib3d from "../Lib3d";
import { getNode } from "./dataHelpers";
import { animateHorizontal, animateVertical } from "./uiHelpers";

export const getCharacterCoords = (c: Character, game: GameInstance | SubInstance): {x: number, y: number} => {
    const currentNodeId = game?.characterLocations.find(l => l.charId === c.id)?.nodeId
    if (currentNodeId) {
        const currentNode = game?.map.nodes.find(n => n.id === currentNodeId)
        if (currentNode) {
            return {x: currentNode.x, y: currentNode.y}
        }
    }
    return {x: 0, y: 0}
}

export const reRenderCharacter = (character: Character, gameInstance: GameInstance) => {
    const characterGroup = scene.getObjectByProperty("name", `char_${character.id}`)
    if (!characterGroup) return
    const baseMapGroup = scene.getObjectByName("baseMap")
    if (!baseMapGroup) return

    baseMapGroup.remove(characterGroup)
    const {x, y} = getCharacterCoords(character, gameInstance)
    baseMapGroup.add(lib3d.pos(x, mdlHeights.characters, y, lib3d.character(character, gameInstance.map.characterScale ?? 5)));
}

export const getCharacterWithLocation = (game: GameInstance, charId: string): {nodeId: string, subMapId?: string} => {
    const cLoc = game.characterLocations.find(c => c.charId === charId)
    return { nodeId: cLoc?.nodeId ?? "", subMapId: cLoc?.subInstanceId }
}

export const getCharacterModelCoords = (scene: THREE.Scene, game: GameInstance, charId: string): {x: number, z: number, y: number} => {
    const loc = getCharacterWithLocation(game, charId)
    const group = loc.subMapId ? scene.getObjectByName(`submap_${loc.subMapId}`) : scene.getObjectByName("baseMap")
    const cModel = group && group.getObjectByName(`char_${charId}`)
    if (cModel) {
        return cModel.getWorldPosition(new THREE.Vector3())
    }
    return {x: 0, y: 0, z: 0}
}

export const getNodeModelCoords = (nodeId: string, subId?: string): {x: number, z: number, y: number} => {
    const group = (subId !== undefined) ? scene.getObjectByName(`submap_${subId}`) : scene.getObjectByName("baseMap")
    const nModel = group && group.getObjectByName(`node_${nodeId}`)
    if (nModel) {
        return nModel.getWorldPosition(new THREE.Vector3())
    }
    return {x: 0, y: 0, z: 0}
}

export const moveCharacterToNode = (scene: THREE.Scene, animations: GameAnimation[] = [], characterId: string, nodeId: string, dur: number) => {
    const characterGroup = scene.getObjectByName(`char_${characterId}`)
    // Remove move buttons
    const btns = characterGroup?.getObjectsByProperty("name", "moveBtns")
    for (const b of btns ?? []) {
        characterGroup?.remove(b)
    }
    // if (btns) {
    //     characterGroup?.remove(btns)
    // }
    if (!characterGroup) { return; }
    const nodeModel = scene.getObjectByName(`node_${nodeId}`)
    if (!nodeModel) { return; }

    animations.filter(a => a.id === `char_${characterId}`).forEach(a => a.ani?.stop())

    animations.push({
        id: `char_${characterId}`,
        ani: new TWEEN.Tween(characterGroup.position).to({ x: nodeModel.position.x, y: mdlHeights.characters, z: nodeModel.position.z }, dur)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                animateHorizontal(characterGroup, .1, 1000)
                cleanAnimations()
            })
    })
}

export const moveCharacterToNodeInSubMap = (scene: THREE.Scene, animations: GameAnimation[] = [], characterId: string, subMapId: string, nodeId: string, dur: number) => {
    const subMapGroup = scene.getObjectByName(`submap_${subMapId}`)
    if (!subMapGroup) { console.log("no submap group"); return }
    const charModel = subMapGroup.getObjectByName(`char_${characterId}`)
    if (!charModel) { console.log("no char"); return }
    const nodeModel = subMapGroup.getObjectByName(`node_${nodeId}`)
    if (!nodeModel) { console.log("no node", subMapGroup); return }

    animations.filter(a => a.id === `char_${characterId}`).forEach(a => a.ani?.stop())

    animations.push({
        id: "",
        ani: new TWEEN.Tween(charModel.position).to({ x: nodeModel.position.x, y: mdlHeights.characters, z: nodeModel.position.z }, dur)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
                animateHorizontal(charModel, .1, 1000)
                cleanAnimations()
            })
    }

    )
}

export const displaySubMap = (subInstanceId: string) => {
    let delay = 0
    // Hide other submaps
    for (const o of scene.children) {
        if (o.userData.type === "submap" && o.visible && o.userData.subInstanceId !== subInstanceId) {
            hideSubMap(o.userData.subInstanceId)
            delay = durations.openSubMap
        }
    }

    const subInstance = expGameInstance?.subInstances.find(s => s.id === subInstanceId)
    if (!subInstance) { console.log("no subinstance"); return }
    const subMapGroup = scene.getObjectByName(`submap_${subInstanceId}`)
    if (!subMapGroup) { console.log("display: no submap", subInstanceId); return }
    // (subInstance.map.mapScale ?? 100) / 100, 1, (subInstance.map.mapScale ?? 100) / 100
    subMapGroup.scale.set(0, subMapGroup.scale.y, 0)
    subMapGroup.visible = true
    setTimeout(() => {
        animations.push({
            id: "",
            ani: new TWEEN.Tween(subMapGroup.scale).to({ x: ((subInstance.map.mapScale ?? 100) / 100), y: subMapGroup.scale.y, z: (subInstance.map.mapScale ?? 100) / 100 }, durations.openSubMap)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => { renderSubMapCharactersBySubId(subInstanceId); cleanAnimations(); })
        })
    }, delay);
}

export const hideSubMap = (subMapId: string) => {
    const subMapGroup = scene.getObjectByName(`submap_${subMapId}`)
    if (!subMapGroup) { console.log("hide: no submap", subMapId); return }
    animations.push(
        {
            id: "",
            ani: new TWEEN.Tween(subMapGroup.scale).to({ x: 0, y: subMapGroup.scale.y, z: 0 }, durations.openSubMap)
            .easing(Easing.Quadratic.InOut)
            .start()
            .onComplete(() => { renderSubMapCharactersBySubId(subMapId); cleanAnimations(); })
        })
}

export const renderSubMapCharactersBySubId = (subId: string) => {
    const subInstance = expGameInstance?.subInstances.find(s => s.id === subId)
    subInstance && renderSubMapCharacters(subInstance)
}

export const renderSubMapCharacters = (subInstance: SubInstance) => {
    const group = scene.getObjectByName(`submap_${subInstance.id}`)
    if (!group) { console.log("no submap group"); return }
    for (const obj of group.children) {
        if (obj.userData.type === "character") {
            obj.parent?.remove(obj)
        }
    }
    for (const c of subInstance.characters) {
        const {x, y} = getCharacterCoords(c, subInstance)
        const char = lib3d.character(c, subInstance.map.characterScale ?? 5)
        // char.scale.set(char.scale.x / ((subInstance.map.mapScale ?? 1) / 100), char.scale.y, char.scale.z)
        char.scale.set(char.scale.x, char.scale.y * ((subInstance.map.mapScale ?? 1) / 100), char.scale.z)
        group.add(lib3d.pos(x, mdlHeights.characters, y, char));
    }
}

export const renderNodeItems = (nodeId: string, subInstanceId?: string, nodeGroup?: THREE.Object3D) => {
    // const nodeGroup = mapGroup?.getObjectByName(`node_${nodeId}`)
    if (!nodeGroup) {
        const mapGroup = (subInstanceId !== undefined) ? scene.getObjectByName(`submap_${subInstanceId}`) : scene.getObjectByName("baseMap")
        if (!mapGroup) { console.log("no map group"); return }
        nodeGroup = mapGroup?.getObjectByName(`node_${nodeId}`)
    }
    if (!nodeGroup) { console.log("no node group"); return }
    const instance = subInstanceId ? expGameInstance?.subInstances.find(s => s.id === subInstanceId) : expGameInstance

    // Remove old items from group
    nodeGroup.children = [...nodeGroup.children.filter(o => !o.userData.isNodeIcon)]

    const node = getNode(nodeId, subInstanceId)
    if (!node) { return }

    const items = instance?.items.filter(i => i.nodeId === nodeId).map(i => i.item) ?? []

    // const mapScale = instance?.map.mapScale ?? 100

    // const iconSize = 1 / ((mapScale ?? 100) / 100) * 1.5
    const iconSize = (instance?.map.characterScale ?? 4) / 2.5

    let startPoint = 0 - ((items.length - 1) * (iconSize/2)) / 2
    // Render items
    items.length > 0 && console.log("items")
    let c = 0
    for (const item of items) {
        const itmGrp = new THREE.Group()
        const bg = lib3d.getImageSquare("img/assets/ui/node_empty_01.png", iconSize, iconSize)
        const itm = lib3d.getImageSquare(item.img, iconSize*.75, iconSize*.75)
        bg.position.set(startPoint, mdlHeights.nodes_items + 0.01, iconSize*0.8)
        itm.position.set(startPoint, mdlHeights.nodes_items + 0.02, iconSize*0.8)
        startPoint += iconSize/2
        itm.userData.isNodeIcon = true
        bg.userData.isNodeIcon = true
        bg.userData.type = "item"
        bg.userData.id = item.id
        itmGrp.add(bg)
        itmGrp.add(itm)
        itmGrp.userData.isNodeIcon = true
        itmGrp.position.set(itmGrp.position.x, c, itmGrp.position.z)
        nodeGroup.add(itmGrp)
        // nodeGroup.add(itm)
        setTimeout(() => {
            animateHorizontal(itmGrp, iconSize/10, 750)
        }, 100)
        c += 0.1
        console.log("C", c, "")
    }

    // Render monsters
    const monsters = instance?.monsters.filter(i => i.nodeId === nodeId).map(i => i.monster) ?? []
    let iX = 0;
    for (const monster of monsters) {
        const itm = lib3d.monster(monster, instance?.map.characterScale ?? 5)
        itm.position.set(itm.position.x + (iconSize/1) + (iconSize/2*iX), 0.05 + (0.01*iX), itm.position.z-(iconSize/2))
        itm.userData.isNodeIcon = true
        nodeGroup.add(itm)
        iX += 1;
        setTimeout(() => {
            animateHorizontal(itm, iconSize/10, 750)
        }, 100)

    }

    // Render NPCs
    const npcs = instance?.npcs.filter(i => i.nodeId === nodeId).map(i => i.npc) ?? []
    for (const npc of npcs) {
        const itm = lib3d.npc(npc, instance?.map.characterScale ?? 5)
        itm.position.set(itm.position.x+(iconSize/2), 0.05, itm.position.z-(iconSize/2))
        itm.userData.isNodeIcon = true
        nodeGroup.add(itm)
        setTimeout(() => {
            animateHorizontal(itm, iconSize/10, 750)
        }, 100)
    }

    // Render clues
    if (node.clueIds) {
        let c = 0
        let clueSP = 0 - ((node.clueIds.length - 1) * (iconSize/2)) / 2
        for (const clueId of node.clueIds) {
            const clue = lib3d.getImageSquare("img/assets/ui/clue_01.png", iconSize, iconSize)
            // clue.position.set(clueSP, .015 + c, -(iconSize*0.8))
            clue.position.set(-(iconSize*1.5), .015 + c, clueSP)
            clue.userData.isNodeIcon = true
            clue.userData.type = "clue"
            clue.userData.id = clueId
            nodeGroup.add(clue)
            clueSP += iconSize/2
            setTimeout(() => {
                animateVertical(clue, iconSize/10, 750)
            }, 100)
            c += 0.01
        }
    }

    // Render gates
    if (node.gateIds) {
        let c = 0
        let gateSP = 0 - ((node.gateIds.length - 1) * (iconSize/2)) / 2
        for (const gateId of node.gateIds) {
            const gate = lib3d.getImageSquare("img/assets/ui/portal_01.png", iconSize*2.25, iconSize*2.25)
            gate.position.set(gateSP, .012 + c, -(iconSize*1.5))
            gate.userData.isNodeIcon = true
            gate.userData.type = "gate"
            gate.userData.id = gateId
            nodeGroup.add(gate)
            gateSP += iconSize/2
            c += 0.01
        }
    }

}